// Pure logic of the AI App Wizard modal, kept out of the component so it can
// be unit-tested: caption prefill for repo-frame selection and assembly of the
// draft handed back to the Create tab.

import type {
  WizardCopyResult, WizardLocalizeInput, WizardLocShotResult, WizardLocTextResult, WizardTask,
} from '@/api/apps-setup';

export interface WizardSceneLike { caption: string }

/**
 * Repo mode: captions are prefilled from the plan's scenes in the order the
 * frames were selected. A caption the operator already typed (even an emptied
 * one) is never overwritten; unselected frames keep whatever they had.
 */
export function assignRepoCaptions(
  scenes: WizardSceneLike[],
  selectedIds: number[],
  current: Record<number, string>,
): Record<number, string> {
  const next: Record<number, string> = { ...current };
  selectedIds.forEach((id, order) => {
    if (next[id] === undefined) next[id] = scenes[order]?.caption ?? '';
  });
  return next;
}

export interface AppWizardDraft {
  name: string;
  short_description: string;
  full_description: string;
  bundle_id: string;
  icon_url: string;
  screenshot_keys: string[];
  /** MEDIA key of the feature graphic, empty when there is none. */
  feature_graphic_key: string;
  stub_id: string;
  /** The wizard job the draft came from — createSkeleton stores its localizations as a listing package. Empty in manual mode. */
  wizard_job_id: string;
}

export interface WizardDraftParts {
  copy: WizardCopyResult | null;
  iconUrl: string;
  screenshotKeys: string[];
  featureKey: string;
  stubId: string;
  jobId: string;
}

/**
 * The draft is usable once there is a name and at least one image. In the AI
 * flow the name always arrives with the copy task; in the manual flow the
 * operator types it, so an empty copy card must not unlock the button.
 */
export function canUseWizardDraft(parts: WizardDraftParts): boolean {
  const named = Boolean(parts.copy?.name.trim());
  const hasImage = Boolean(parts.iconUrl) || parts.screenshotKeys.length > 0 || Boolean(parts.featureKey);
  return named && hasImage;
}

/**
 * Early create: the build only needs the name, the bundle and the icon, so the
 * app can exist — and be connected and built — while the frames, texts and
 * localizations are still generating. Nothing else is required here.
 */
export function canCreateAppEarly(parts: Pick<WizardDraftParts, 'copy' | 'iconUrl'>): boolean {
  return Boolean(parts.copy?.name.trim()) && Boolean(parts.copy?.bundle_id.trim()) && Boolean(parts.iconUrl);
}

export function buildWizardDraft(parts: WizardDraftParts): AppWizardDraft | null {
  if (!canUseWizardDraft(parts) || !parts.copy) return null;
  return {
    name: parts.copy.name,
    short_description: parts.copy.short_description,
    full_description: parts.copy.full_description,
    bundle_id: parts.copy.bundle_id,
    icon_url: parts.iconUrl,
    screenshot_keys: [...parts.screenshotKeys],
    feature_graphic_key: parts.featureKey,
    stub_id: parts.stubId,
    wizard_job_id: parts.jobId,
  };
}

// ---- Localization run ----

/** One current screenshot of the result screen, in display order. */
export interface LocalizeShotInput { key?: string; url: string; caption: string }

/**
 * The localize request is built from the CURRENT edited state of the result
 * screen: the copy card (name maps to the listing title) and the screenshots
 * as they stand now — AI frames with their edited captions, or repo frames
 * already composed and uploaded. Position is the display order.
 */
export function buildLocalizePayload(
  langs: string[],
  copy: WizardCopyResult,
  shots: LocalizeShotInput[],
): WizardLocalizeInput {
  return {
    langs: [...langs],
    title: copy.name,
    short_description: copy.short_description,
    full_description: copy.full_description,
    captions: shots.map((s, position) => ({
      ...(s.key ? { key: s.key } : {}),
      url: s.url,
      caption: s.caption,
      position,
    })),
  };
}

export interface LangLocProgress {
  lang: string;
  /** 'pending' also covers 'running'. A failed loc_text with no lang in its result cannot be attributed. */
  text: 'pending' | 'done' | 'failed';
  /** false — the localized text overflows Play limits; null until the text lands. */
  fits: boolean | null;
  shotsDone: number;
  shotsFailed: number;
  shotsTotal: number;
  complete: boolean;
}

type LocTaskLike = Pick<WizardTask, 'kind' | 'status' | 'result'> & { lang?: string };

/** The backend exposes the payload lang on every loc task; results carry it too. */
function langOf(t: LocTaskLike): string | undefined {
  return t.lang ?? (t.result as { lang?: string } | null)?.lang ?? undefined;
}

/**
 * Per-language progress of a localization run, aggregated from loc_* tasks.
 * The caller passes the langs it requested; langs discovered on tasks are
 * merged in for a job rejoined after a reload.
 */
export function locProgressByLang(tasks: LocTaskLike[], requestedLangs: string[]): LangLocProgress[] {
  const textTasks = tasks.filter((t) => t.kind === 'loc_text');
  const shotTasks = tasks.filter((t) => t.kind === 'loc_shot');

  const langs: string[] = [...requestedLangs];
  const addLang = (lang: string) => { if (!langs.includes(lang)) langs.push(lang); };
  for (const t of [...textTasks, ...shotTasks]) {
    const lang = langOf(t);
    if (lang) addLang(lang);
  }
  if (langs.length === 0) return [];

  const langCount = Math.max(textTasks.length, langs.length, 1);
  const shotsTotal = Math.round(shotTasks.length / langCount);

  return langs.map((lang) => {
    const text = textTasks.find((t) => langOf(t) === lang);
    const textDone = text?.status === 'done';
    const textFailed = text?.status === 'failed';
    const fits = textDone ? (text!.result as WizardLocTextResult).fits : null;
    const shots = shotTasks.filter((t) => langOf(t) === lang);
    const shotsDone = shots.filter((t) => t.status === 'done').length;
    const shotsFailed = shots.filter((t) => t.status === 'failed').length;
    return {
      lang,
      text: textDone ? 'done' as const : textFailed ? 'failed' as const : 'pending' as const,
      fits,
      shotsDone,
      shotsFailed,
      shotsTotal,
      complete: textDone && shotsDone >= shotsTotal,
    };
  });
}

/** True once any loc_* task exists — the run was started (possibly in another session). */
export function hasLocTasks(tasks: LocTaskLike[]): boolean {
  return tasks.some((t) => t.kind === 'loc_text' || t.kind === 'loc_shot');
}

/** One finished localization as the result screen shows it: texts plus the re-rendered frames in order. */
export interface LocalizedView extends WizardLocTextResult {
  shots: WizardLocShotResult[];
}

/**
 * The locales whose text is done, in the order their tasks were created, each
 * with the frames rendered so far. A locale appears as soon as its text lands;
 * frames still rendering simply are not in `shots` yet.
 */
export function localizedViews(tasks: LocTaskLike[]): LocalizedView[] {
  const views: LocalizedView[] = [];
  for (const t of tasks) {
    if (t.kind !== 'loc_text' || t.status !== 'done' || !t.result) continue;
    const text = t.result as WizardLocTextResult;
    const lang = langOf(t) ?? text.lang;
    if (!lang || views.some((v) => v.lang === lang)) continue;
    const shots = tasks
      .filter((s) => s.kind === 'loc_shot' && s.status === 'done' && s.result && langOf(s) === lang)
      .map((s) => s.result as WizardLocShotResult)
      .sort((a, b) => a.position - b.position);
    views.push({ ...text, lang, shots });
  }
  return views;
}
